import React, { useMemo, useState } from "react";
import { ChevronRight, ChevronDown } from "lucide-react";
import { T, STATUS } from "../theme/tokens.js";
import { Btn, Chip, Empty } from "./ui/index.jsx";
import { scheduleTree, flattenTree } from "../lib/calc.js";
import { fmtDate } from "../lib/format.js";

/* A 546-row export is a list only a scheduler can read. It is a plan, and a
 * plan is legible when you can see the shape first and open the part you care
 * about: ten phases, then the workstreams inside a phase, then the tasks.
 *
 * Every summary row carries the rollup of what is beneath it, so a phase can be
 * judged without opening it at all. That is the difference between a list and
 * something a client can be shown.
 */

/* The same four colours the registers use. "Due to start" and "Not started"
   share the blue on purpose: both are work nobody has begun, and the label is
   what separates them. A fifth hue that reads as neither amber nor blue does
   not exist on this surface, and inventing one would make the four mean less
   everywhere else. */
const HEALTH = {
  complete:       { label: "Complete", color: STATUS.complete.ink },
  late:           { label: "Late", color: STATUS.overdue.ink },
  "in progress":  { label: "In progress", color: STATUS.inProgress.ink },
  "due to start": { label: "Due to start", color: STATUS.notStarted.ink },
  "not started":  { label: "Not started", color: STATUS.notStarted.ink },
};

function Bar({ complete, leaves }) {
  const pct = leaves ? Math.round((complete / leaves) * 100) : 0;
  return (
    <div title={`${complete} of ${leaves} complete`}
      style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ width: 68, height: 7, background: T.line + "88",
                    borderRadius: 4, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: STATUS.complete.fill }} />
      </div>
      <span style={{ fontSize: 11.5, color: T.soft, whiteSpace: "nowrap" }}>{pct}%</span>
    </div>
  );
}

export default function ScheduleTree({ rows = [] }) {
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [openedOnce, setOpenedOnce] = useState(false);

  const roots = useMemo(() => scheduleTree(rows), [rows]);

  /* Open the top two levels the first time, so the plan reads as its phases
     rather than as a single unopened row. */
  const initial = useMemo(() => {
    const ids = new Set();
    const visit = (n, d) => {
      if (d < 2 && n.children.length) ids.add(n.id);
      n.children.forEach((c) => visit(c, d + 1));
    };
    roots.forEach((r) => visit(r, 0));
    return ids;
  }, [roots]);

  const open = openedOnce ? expanded : initial;

  const setOpen = (next) => { setExpanded(next); setOpenedOnce(true); };
  const toggle = (id) => {
    const next = new Set(open);
    if (next.has(id)) next.delete(id); else next.add(id);
    setOpen(next);
  };

  const allIds = useMemo(() => {
    const ids = new Set();
    const visit = (n) => { if (n.children.length) ids.add(n.id); n.children.forEach(visit); };
    roots.forEach(visit);
    return ids;
  }, [roots]);

  const q = query.trim().toLowerCase();
  const match = q
    ? (n) => `${n.title} ${n.activity_id ?? ""} ${n.workstream ?? ""}`.toLowerCase().includes(q)
    : null;

  const visible = useMemo(
    () => flattenTree(roots, open, { match }),
    [roots, open, q], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const late = useMemo(() => {
    let n = 0;
    const visit = (x) => { if (!x.children.length && x.health === "late") n += 1; x.children.forEach(visit); };
    roots.forEach(visit);
    return n;
  }, [roots]);

  if (!rows.length) {
    return <Empty text="No schedule loaded for this contract yet. Use the Import page." />;
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap",
                    marginBottom: 12 }}>
        <input value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Find an activity, a WBS number or a workstream"
          style={{
            flex: "1 1 260px", padding: "6px 10px", border: `1px solid ${T.line}`,
            borderRadius: 4, fontSize: 13, background: "#fff", color: T.ink,
          }} />
        <Btn small kind="ghost" onClick={() => setOpen(new Set(allIds))}>Open all</Btn>
        <Btn small kind="ghost" onClick={() => setOpen(new Set())}>Close all</Btn>
        {late > 0 && <Chip color={T.risk}>{late} late</Chip>}
      </div>

      {visible.length === 0 ? (
        <Empty text={`Nothing matches "${query}".`} />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                {["Activity", "WBS", "Starts", "Finishes", "State", "Progress"].map((h, i) => (
                  <th key={h} style={{
                    textAlign: i >= 2 && i <= 3 ? "right" : "left", padding: "7px 10px",
                    borderBottom: `2px solid ${T.navy}`, color: T.navy, fontSize: 11,
                    fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
                    whiteSpace: "nowrap",
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((n) => {
                const isParent = n.children.length > 0;
                const isOpen = open.has(n.id);
                const health = HEALTH[n.health] ?? HEALTH["not started"];
                return (
                  <tr key={n.id} style={{
                    borderBottom: `1px solid ${T.line}`,
                    background: n.health === "late" && !isParent ? T.risk + "0D" : "transparent",
                  }}>
                    <td style={{ padding: "6px 10px", minWidth: 260 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6,
                                    paddingLeft: n.depth * 16 }}>
                        {isParent ? (
                          <button onClick={() => toggle(n.id)} title={isOpen ? "Collapse" : "Expand"}
                            style={{
                              border: "none", background: "transparent", cursor: "pointer",
                              padding: 0, display: "flex", color: T.navy,
                            }}>
                            {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                          </button>
                        ) : (
                          <span style={{ width: 15, display: "inline-block" }} />
                        )}
                        <span style={{
                          fontWeight: isParent ? 700 : 400,
                          color: isParent ? T.navy : T.ink,
                          overflowWrap: "anywhere",
                        }}>{n.title}</span>
                        {isParent && (
                          <span style={{ fontSize: 11.5, color: T.soft, whiteSpace: "nowrap" }}>
                            {n.stats.leaves} activit{n.stats.leaves === 1 ? "y" : "ies"}
                            {n.stats.late ? `, ${n.stats.late} late` : ""}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: "6px 10px", color: T.soft, whiteSpace: "nowrap" }}>
                      {n.activity_id ?? ""}
                    </td>
                    <td style={{ padding: "6px 10px", textAlign: "right", whiteSpace: "nowrap",
                                 fontWeight: n.start_is_actual ? 700 : 400 }}
                        title={n.start_is_actual ? "Actual start" : "Scheduled"}>
                      {fmtDate(n.start_date)}
                    </td>
                    <td style={{ padding: "6px 10px", textAlign: "right", whiteSpace: "nowrap",
                                 fontWeight: n.finish_is_actual ? 700 : 400 }}
                        title={n.finish_is_actual ? "Actual finish" : "Scheduled"}>
                      {fmtDate(n.finish_date)}
                    </td>
                    <td style={{ padding: "6px 10px", whiteSpace: "nowrap" }}>
                      <Chip color={health.color}>{health.label}</Chip>
                    </td>
                    <td style={{ padding: "6px 10px" }}>
                      <Bar complete={n.stats.complete} leaves={n.stats.leaves} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ fontSize: 12, color: T.soft, marginTop: 10, lineHeight: 1.6 }}>
        A phase carries the rollup of everything under it, so it can be judged
        without opening it. Late means the finish date has passed and the
        activity is not complete. Dates in bold are actual rather than
        scheduled. Searching opens whatever it finds.
      </div>
    </div>
  );
}
